import type { CurrencyCodeType } from '@mint-lib/currencies-helpers';
import {
  useCurrencyFormatter,
  useNumberFormatter,
  usePercentFormatter,
} from '@mint-lib/formatting-hooks';
import {
  Add,
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Equal,
  StarFilled,
  Subtract,
} from '@mint-lib/icons';
import { Card, Chip, typographyVariants } from '@mint-lib/tw-components';
import type { Suggestions } from '@mint-sdk/api-recommendations';

import { getProposedValueImpact } from '../../../utils/kpiWidget.js';

type WidgetCardProps = {
  widget: Suggestions.ForecastedKPIItemType;
};

const iconMap: Record<string, React.ReactNode> = {
  Equal: <Equal />,
  Add: <Add />,
  Subtract: <Subtract />,
  ArrowDown: <ArrowDown />,
  ArrowUp: <ArrowUp />,
};

const WidgetCard = ({ widget }: WidgetCardProps) => {
  const { color, sign, percentageSign } = getProposedValueImpact(widget);

  const { format: currencyFormatter } = useCurrencyFormatter();
  const { format: percentFormatter } = usePercentFormatter();
  const { format: numberFormatter } = useNumberFormatter();

  const getMetricValue = (value: number): string => {
    if (widget.metricType === 'cost_based') {
      return (
        currencyFormatter?.(Number(value), {
          currency: widget.currency as CurrencyCodeType,
          maxDecimals: 2,
        })?.formatted ?? ''
      );
    }
    if (widget.metricType === 'percentage') {
      return (
        percentFormatter?.(Number(value), {
          maxDecimals: 2,
        })?.formatted ?? ''
      );
    }

    return (
      numberFormatter?.(Number(value), {
        options: {
          maxDecimals: 2,
        },
      })?.formatted ?? ''
    );
  };

  return (
    <Card
      contentClassName="flex flex-col w-full gap-2"
      variant="outlined"
      className="flex w-full flex-col"
    >
      <div className="flex flex-row gap-2">
        {widget.isPrimary && <StarFilled className="fill-icon-primary" />}
        <p
          className={typographyVariants({ variant: 'h6', color: 'secondary' })}
        >
          {widget.label}
        </p>
      </div>
      <div className="flex flex-row justify-between">
        <p className={typographyVariants({ variant: 'h4' })}>
          {getMetricValue(widget.currentValue)}
        </p>
        <ChevronRight className="size-4 fill-icon-primary" />
        <p className={typographyVariants({ variant: 'h4', color: color })}>
          {getMetricValue(widget.proposedValue)}
        </p>
      </div>
      <div className="flex flex-row gap-1">
        <Chip
          size="small"
          variant="filled"
          color={color}
          className="flex flex-row gap-0.5"
        >
          {iconMap[percentageSign]}
          {percentFormatter?.(Math.abs(widget.percentageChange ?? 0), {
            maxDecimals: 2,
          })?.formatted ?? ''}
        </Chip>
        <Chip color="silver" size="small" className="flex flex-row gap-0.5">
          {iconMap[sign]}
          {getMetricValue(Math.abs(widget.absoluteChange))}
        </Chip>
      </div>
    </Card>
  );
};

export default WidgetCard;
